import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ArrowCounterClockwise,
  ArrowDown,
  ArrowLeft,
  ArrowSquareOut,
  EyeSlash,
  Flag,
  ThumbsUp,
  TrashSimple,
} from "@phosphor-icons/react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  type BadgeVariant,
  IconButton,
  initialsForMember,
  LoadingOverlay,
  MetricCard,
  type MetricCardProps,
  Textarea,
} from "@breakingthelines/design-system";

import { siteBaseUrl } from "@/config/env";
import {
  flagContent,
  getContent,
  getContentFlags,
  moderateContent,
  reviewContentFlag,
  setContentLinkRelOverrides,
  type AdminContentItem,
  type ContentFlagItem,
  type ModerationActionValue,
} from "@/lib/admin-api";

import styles from "./content-detail.module.css";

function formatType(type?: string) {
  if (!type) return "—";
  return type
    .replace("CONTENT_TYPE_", "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatStatus(status?: string) {
  if (!status) return "—";
  return status
    .replace("CONTENT_STATUS_", "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatVisibility(visibility?: string) {
  if (!visibility) return "—";
  return visibility
    .replace("VISIBILITY_LEVEL_", "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatDate(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatFlagReason(reason: string): string {
  const map: Record<string, string> = {
    FLAG_REASON_SPAM: "Spam",
    FLAG_REASON_HARASSMENT: "Harassment",
    FLAG_REASON_MISINFORMATION: "Misinformation",
    FLAG_REASON_INAPPROPRIATE: "Inappropriate",
    FLAG_REASON_COPYRIGHT: "Copyright",
    FLAG_REASON_OTHER: "Other",
    FLAG_REASON_TYPO: "Typo",
    FLAG_REASON_FACTUAL_ERROR: "Factual Error",
    FLAG_REASON_TTS_PRONUNCIATION: "TTS Pronunciation",
    FLAG_REASON_SUGGESTION: "Suggestion",
  };
  return map[reason] ?? reason;
}

function formatFlagStatus(status: string): string {
  const map: Record<string, string> = {
    FLAG_STATUS_PENDING: "Pending",
    FLAG_STATUS_REVIEWED: "Reviewed",
    FLAG_STATUS_DISMISSED: "Dismissed",
    FLAG_STATUS_ACKNOWLEDGED: "Acknowledged",
    FLAG_STATUS_FORWARDED: "Forwarded",
  };
  return map[status] ?? status;
}

function flagStatusVariant(status: string): BadgeVariant {
  if (status === "FLAG_STATUS_PENDING") return "tintedWarn";
  if (status === "FLAG_STATUS_REVIEWED") return "secondary";
  return "tintedSuccess";
}

// Unset is MODERATION_STATUS_NONE by contract, so an item with no moderation
// state reads as "None" rather than as an unknown.
function formatModerationStatus(status?: string): string {
  const map: Record<string, string> = {
    MODERATION_STATUS_UNSPECIFIED: "None",
    MODERATION_STATUS_NONE: "None",
    MODERATION_STATUS_DERANKED: "Deranked",
    MODERATION_STATUS_HIDDEN: "Hidden",
    MODERATION_STATUS_REMOVED: "Removed",
  };
  if (!status) return "None";
  return map[status] ?? status;
}

function isModerated(status?: string): boolean {
  return (
    status != null &&
    status !== "MODERATION_STATUS_NONE" &&
    status !== "MODERATION_STATUS_UNSPECIFIED"
  );
}

// The per-item moderation actions, available whether or not a flag exists. This
// is the gap these buttons close: they used to live INSIDE the flag list and
// render only for a PENDING flag, so on unflagged content the page offered
// nothing but "Flag" — which read as "this page only lets me flag things".
const MODERATION_ACTIONS: Array<{
  key: string;
  action: ModerationActionValue;
  label: string;
  title: string;
  icon: ReactNode;
  danger?: boolean;
}> = [
  {
    key: "derank",
    action: "MODERATION_ACTION_DERANK",
    label: "Derank",
    title: "Remove from discovery. The URL still resolves and it stays on the author's profile.",
    icon: <ArrowDown size={16} />,
  },
  {
    key: "hide",
    action: "MODERATION_ACTION_HIDE",
    label: "Hide",
    title: "Remove from public surfaces. It stays on the author's own profile.",
    icon: <EyeSlash size={16} />,
  },
  {
    key: "remove",
    action: "MODERATION_ACTION_REMOVE",
    label: "Remove",
    title: "404s everywhere, including the author's own profile. For severe cases.",
    icon: <TrashSimple size={16} />,
    danger: true,
  },
  {
    key: "restore",
    action: "MODERATION_ACTION_DISMISS",
    label: "Restore",
    title: "Clear any moderation state and return the item to normal visibility.",
    icon: <ArrowCounterClockwise size={16} />,
  },
];

function statusBadgeClass(status?: string): string {
  if (status === "CONTENT_STATUS_PUBLISHED") return styles.badgePublished;
  if (status === "CONTENT_STATUS_DRAFT") return styles.badgeDraft;
  return "";
}

export function ContentDetailPage() {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState<AdminContentItem | null>(null);
  const [flags, setFlags] = useState<ContentFlagItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [moderationNotice, setModerationNotice] = useState<string | null>(null);
  const [linkRelError, setLinkRelError] = useState<string | null>(null);
  const [nofollowConfirmOpen, setNofollowConfirmOpen] = useState(false);
  const [nofollowNote, setNofollowNote] = useState("");

  useEffect(() => {
    if (!contentId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([getContent(contentId), getContentFlags(contentId)])
      .then(([contentData, flagsData]) => {
        if (cancelled) return;
        setContent(contentData);
        setFlags(flagsData.items ?? []);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Failed to load content");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contentId]);

  const handleReviewFlag = (
    flagId: string,
    status: string,
    moderationAction: ModerationActionValue = "MODERATION_ACTION_HIDE",
  ) => {
    setActionLoading(flagId);
    reviewContentFlag({ flagId, status, moderationAction })
      .then(() => {
        setFlags((prev) => prev.map((f) => (f.id === flagId ? { ...f, status } : f)));
      })
      .catch((err: Error) => {
        setError(err.message ?? "Action failed");
      })
      .finally(() => {
        setActionLoading(null);
      });
  };

  // Direct per-item moderation: no flag needs to exist, and none is created for
  // the operator to then have to review. The response says what actually
  // changed, so a repeated click reports "already Deranked" instead of silently
  // looking like a fresh takedown.
  const handleModerate = (key: string, moderationAction: ModerationActionValue) => {
    if (!contentId) return;
    setActionLoading(`moderate-${key}`);
    setModerationNotice(null);
    moderateContent({ contentId, moderationAction })
      .then((result) => {
        setContent((prev) =>
          prev ? { ...prev, moderationStatus: result.moderationStatus } : prev,
        );
        const now = formatModerationStatus(result.moderationStatus);
        setModerationNotice(
          result.changed
            ? `Moderation set to ${now} (was ${formatModerationStatus(result.previousModerationStatus)}).`
            : `Already ${now} — nothing changed.`,
        );
        // The action writes an audit flag, so the history below is now stale.
        return getContentFlags(contentId).then((flagsData) => setFlags(flagsData.items ?? []));
      })
      .catch((err: Error) => {
        setError(err.message ?? "Moderation action failed");
      })
      .finally(() => {
        setActionLoading(null);
      });
  };

  const handleFlag = () => {
    if (!contentId) return;
    setActionLoading("flag-new");
    flagContent({ contentId, reason: "FLAG_REASON_OTHER", details: "Flagged by admin" })
      .then((data) => {
        setFlags((prev) => [...prev, data.flag]);
      })
      .catch((err: Error) => {
        setError(err.message ?? "Failed to flag content");
      })
      .finally(() => {
        setActionLoading(null);
      });
  };

  const linkRelOverrides = content?.linkRelOverrides ?? {};

  // Saves the full set of three flags every time - the RPC takes all three,
  // not a single-field patch - and refreshes the content on success so the
  // switches and the header badge read the value content-service actually
  // stored.
  const saveLinkRelOverrides = (
    next: { omitSponsored: boolean; omitUgc: boolean; omitNofollow: boolean },
    note: string,
  ) => {
    if (!contentId) return;
    setActionLoading("link-rel-overrides");
    setLinkRelError(null);
    setContentLinkRelOverrides({ contentId, ...next, note })
      .then(() => getContent(contentId))
      .then((data) => setContent(data))
      .catch((err: Error) => {
        setLinkRelError(err.message ?? "Failed to update outbound links");
      })
      .finally(() => {
        setActionLoading(null);
      });
  };

  const handleToggleSponsored = (on: boolean) => {
    saveLinkRelOverrides(
      {
        omitSponsored: !on,
        omitUgc: !!linkRelOverrides.omitUgc,
        omitNofollow: !!linkRelOverrides.omitNofollow,
      },
      "",
    );
  };

  const handleToggleUgc = (on: boolean) => {
    saveLinkRelOverrides(
      {
        omitSponsored: !!linkRelOverrides.omitSponsored,
        omitUgc: !on,
        omitNofollow: !!linkRelOverrides.omitNofollow,
      },
      "",
    );
  };

  // Turning nofollow ON restores the default and needs no confirmation.
  // Turning it OFF grants the linked site ranking equity, so it opens a
  // confirm dialog with a required note instead of saving immediately.
  const handleToggleNofollow = (on: boolean) => {
    if (on) {
      saveLinkRelOverrides(
        {
          omitSponsored: !!linkRelOverrides.omitSponsored,
          omitUgc: !!linkRelOverrides.omitUgc,
          omitNofollow: false,
        },
        "",
      );
      return;
    }
    setNofollowNote("");
    setNofollowConfirmOpen(true);
  };

  const nofollowNoteValid = nofollowNote.trim().length > 0;

  const handleConfirmRemoveNofollow = () => {
    if (!nofollowNoteValid) return;
    saveLinkRelOverrides(
      {
        omitSponsored: !!linkRelOverrides.omitSponsored,
        omitUgc: !!linkRelOverrides.omitUgc,
        omitNofollow: true,
      },
      nofollowNote.trim(),
    );
    setNofollowConfirmOpen(false);
  };

  const metrics: MetricCardProps[] = useMemo(
    () => [
      { label: "Views", value: (content?.viewCount ?? 0).toLocaleString() },
      { label: "Likes", value: (content?.likeCount ?? 0).toLocaleString() },
      { label: "Thoughts", value: (content?.thoughtCount ?? 0).toLocaleString() },
      { label: "Bookmarks", value: (content?.bookmarkCount ?? 0).toLocaleString() },
      {
        label: "Flags",
        value: flags.length.toLocaleString(),
        delta: `${flags.filter((f) => f.status === "FLAG_STATUS_PENDING").length} pending`,
        deltaTone: flags.some((f) => f.status === "FLAG_STATUS_PENDING") ? "negative" : "neutral",
      },
    ],
    [content, flags],
  );

  const pendingFlags = flags.filter((f) => f.status === "FLAG_STATUS_PENDING");

  return (
    <div className={styles.page}>
      <div className={styles.contentCard}>
        <button type="button" className={styles.backRow} onClick={() => navigate(-1)}>
          <ArrowLeft size={14} />
          Go back
        </button>

        {loading ? <LoadingOverlay size={24} radius="md" label="Loading content..." /> : null}
        {!loading && error ? <div className={styles.errorState}>{error}</div> : null}

        {content ? (
          <>
            <div className={styles.headerSection}>
              <div className={styles.headerInfo}>
                <h1 className={styles.title}>{content.title ?? "Untitled"}</h1>
                <p className={styles.subtitle}>{content.slug ? `/${content.slug}` : contentId}</p>
                <div className={styles.badgeRow}>
                  <span className={`${styles.badge} ${statusBadgeClass(content.status)}`}>
                    {formatStatus(content.status)}
                  </span>
                  <span className={styles.badge}>{formatType(content.type)}</span>
                  <span className={styles.badge}>{formatVisibility(content.visibility)}</span>
                  <span
                    className={`${styles.badge} ${isModerated(content.moderationStatus) ? styles.badgeModerated : ""}`}
                  >
                    Moderation: {formatModerationStatus(content.moderationStatus)}
                  </span>
                  {linkRelOverrides.omitNofollow ? (
                    <span className={`${styles.badge} ${styles.badgeDofollow}`}>dofollow</span>
                  ) : null}
                </div>
                {content.seoDescription ? (
                  <p className={styles.subtitle}>{content.seoDescription}</p>
                ) : null}
              </div>
              {content.seoImageUrl ? (
                <img className={styles.coverImage} src={content.seoImageUrl} alt="" />
              ) : null}
            </div>

            <div className={styles.actions}>
              {MODERATION_ACTIONS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  title={item.title}
                  className={`${styles.actionButton} ${item.danger ? styles.actionButtonDanger : ""}`}
                  onClick={actionLoading ? undefined : () => handleModerate(item.key, item.action)}
                >
                  {item.icon} {item.label}
                </button>
              ))}
              <button
                type="button"
                className={styles.actionButton}
                onClick={actionLoading ? undefined : handleFlag}
              >
                <Flag size={16} /> Flag
              </button>
              {pendingFlags.length > 0 ? (
                <>
                  <button
                    type="button"
                    className={styles.actionButton}
                    onClick={
                      actionLoading
                        ? undefined
                        : () => {
                            for (const f of pendingFlags)
                              handleReviewFlag(f.id, "FLAG_STATUS_REVIEWED");
                          }
                    }
                  >
                    <EyeSlash size={16} /> Review All
                  </button>
                  <button
                    type="button"
                    className={styles.actionButton}
                    onClick={
                      actionLoading
                        ? undefined
                        : () => {
                            for (const f of pendingFlags)
                              handleReviewFlag(f.id, "FLAG_STATUS_DISMISSED");
                          }
                    }
                  >
                    <ThumbsUp size={16} /> Dismiss All
                  </button>
                </>
              ) : null}
              {content.canonicalRoute?.path ? (
                <a
                  href={`${siteBaseUrl()}${content.canonicalRoute.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.actionButton}
                >
                  <ArrowSquareOut size={16} /> View on Site
                </a>
              ) : null}
            </div>

            {moderationNotice ? (
              <p className={styles.moderationNotice}>{moderationNotice}</p>
            ) : null}

            <section className={styles.metricsGrid}>
              {metrics.map((metric) => (
                <MetricCard key={String(metric.label)} {...metric} density="spacious" />
              ))}
            </section>

            <div className={styles.detailGrid}>
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>Details</h3>
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Content ID</span>
                  <span className={styles.fieldValue}>{content.id}</span>
                </div>
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Type</span>
                  <span className={styles.fieldValue}>{formatType(content.type)}</span>
                </div>
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Status</span>
                  <span className={styles.fieldValue}>{formatStatus(content.status)}</span>
                </div>
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Visibility</span>
                  <span className={styles.fieldValue}>{formatVisibility(content.visibility)}</span>
                </div>
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Created</span>
                  <span className={styles.fieldValue}>{formatDate(content.createdAt)}</span>
                </div>
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Published</span>
                  <span className={styles.fieldValue}>{formatDate(content.publishedAt)}</span>
                </div>
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Updated</span>
                  <span className={styles.fieldValue}>{formatDate(content.updatedAt)}</span>
                </div>
              </div>

              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>Creator</h3>
                {content.creator ? (
                  <div className={styles.creatorRow}>
                    <Avatar className={styles.creatorAvatar} aria-hidden="true">
                      {content.creator.avatarUrl ? (
                        <AvatarImage src={content.creator.avatarUrl} alt="" />
                      ) : null}
                      <AvatarFallback>
                        {initialsForMember(
                          content.creator.displayName || content.creator.username || "",
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className={styles.creatorInfo}>
                      <span className={styles.creatorName}>
                        {content.creator.displayName || content.creator.username}
                      </span>
                      {content.creator.username ? (
                        <span className={styles.creatorHandle}>@{content.creator.username}</span>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <p className={styles.subtitle}>Unknown creator</p>
                )}

                {content.authors && content.authors.length > 0 ? (
                  <>
                    <h3 className={styles.sectionTitle} style={{ marginTop: 16 }}>
                      Authors
                    </h3>
                    {content.authors.map((author) => (
                      <div key={author.id} className={styles.creatorRow}>
                        <Avatar className={styles.creatorAvatar} aria-hidden="true">
                          {author.avatarUrl ? <AvatarImage src={author.avatarUrl} alt="" /> : null}
                          <AvatarFallback>
                            {initialsForMember(author.displayName || author.username || "")}
                          </AvatarFallback>
                        </Avatar>
                        <div className={styles.creatorInfo}>
                          <span className={styles.creatorName}>
                            {author.displayName || author.username}
                          </span>
                          {author.username ? (
                            <span className={styles.creatorHandle}>@{author.username}</span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </>
                ) : null}
              </div>

              <div className={`${styles.sectionCard} ${styles.flagsSection}`}>
                <h3 className={styles.sectionTitle}>Outbound links</h3>
                <div className={styles.switchList}>
                  <label className={styles.switchOption}>
                    <input
                      type="checkbox"
                      role="switch"
                      className={styles.switchInput}
                      checked={!linkRelOverrides.omitSponsored}
                      disabled={actionLoading != null}
                      onChange={(event) => handleToggleSponsored(event.currentTarget.checked)}
                    />
                    <span className={styles.switchTrack} aria-hidden="true" />
                    Mark links sponsored
                  </label>
                  <label className={styles.switchOption}>
                    <input
                      type="checkbox"
                      role="switch"
                      className={styles.switchInput}
                      checked={!linkRelOverrides.omitUgc}
                      disabled={actionLoading != null}
                      onChange={(event) => handleToggleUgc(event.currentTarget.checked)}
                    />
                    <span className={styles.switchTrack} aria-hidden="true" />
                    Mark links UGC
                  </label>
                  <label className={styles.switchOption}>
                    <input
                      type="checkbox"
                      role="switch"
                      className={styles.switchInput}
                      checked={!linkRelOverrides.omitNofollow}
                      disabled={actionLoading != null}
                      onChange={(event) => handleToggleNofollow(event.currentTarget.checked)}
                    />
                    <span className={styles.switchTrack} aria-hidden="true" />
                    Mark links nofollow
                  </label>
                </div>
                {linkRelError ? <p className={styles.errorText}>{linkRelError}</p> : null}
              </div>

              <div className={`${styles.sectionCard} ${styles.flagsSection}`}>
                <h3 className={styles.sectionTitle}>Flag History ({flags.length})</h3>
                {flags.length > 0 ? (
                  <div className={styles.flagsList}>
                    {flags.map((flag) => (
                      <div key={flag.id} className={styles.flagItem}>
                        <div className={styles.flagInfo}>
                          <span className={styles.flagReason}>{formatFlagReason(flag.reason)}</span>
                          {flag.details ? (
                            <span className={styles.flagDetails}>{flag.details}</span>
                          ) : null}
                        </div>
                        <div className={styles.flagMeta}>
                          <span className={styles.flagDate}>{formatDate(flag.createdAt)}</span>
                          <Badge variant={flagStatusVariant(flag.status)}>
                            {formatFlagStatus(flag.status)}
                          </Badge>
                          {flag.status === "FLAG_STATUS_PENDING" ? (
                            <div className={styles.flagActions}>
                              <IconButton
                                aria-label="Hide (remove from public surfaces; stays on the author's profile)"
                                variant="ghost"
                                size="md"
                                onClick={
                                  actionLoading === flag.id
                                    ? undefined
                                    : () =>
                                        handleReviewFlag(
                                          flag.id,
                                          "FLAG_STATUS_REVIEWED",
                                          "MODERATION_ACTION_HIDE",
                                        )
                                }
                              >
                                <EyeSlash size={14} />
                              </IconButton>
                              <IconButton
                                aria-label="Derank (remove from discovery; URL still resolves; stays on the author's profile)"
                                variant="ghost"
                                size="md"
                                onClick={
                                  actionLoading === flag.id
                                    ? undefined
                                    : () =>
                                        handleReviewFlag(
                                          flag.id,
                                          "FLAG_STATUS_REVIEWED",
                                          "MODERATION_ACTION_DERANK",
                                        )
                                }
                              >
                                <ArrowDown size={14} />
                              </IconButton>
                              <IconButton
                                aria-label="Dismiss"
                                variant="ghost"
                                size="md"
                                onClick={
                                  actionLoading === flag.id
                                    ? undefined
                                    : () =>
                                        handleReviewFlag(
                                          flag.id,
                                          "FLAG_STATUS_DISMISSED",
                                          "MODERATION_ACTION_DISMISS",
                                        )
                                }
                              >
                                <ThumbsUp size={14} />
                              </IconButton>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyFlags}>No flags on this content.</div>
                )}
              </div>
            </div>
          </>
        ) : null}

        <AlertDialog
          open={nofollowConfirmOpen}
          onOpenChange={(open) => {
            if (open) return;
            setNofollowConfirmOpen(false);
            setNofollowNote("");
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove nofollow from this article's links</AlertDialogTitle>
              <AlertDialogDescription>
                This makes the article's outbound links pass search ranking to the linked sites. For
                a paid placement, this breaches Google's link-spam policy and can trigger a manual
                action against breakingthelines.com.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Note</span>
              <Textarea
                value={nofollowNote}
                onChange={(event) => setNofollowNote(event.currentTarget.value)}
                rows={3}
              />
            </label>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                disabled={!nofollowNoteValid}
                onClick={handleConfirmRemoveNofollow}
              >
                Remove nofollow
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
